import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, Plus, X, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  kind: "tags" | "topics";
  workspaceId: string;
  value: string[];
  onChange: (names: string[]) => void;
  onManage?: () => void;
}

const labels = {
  tags: { singular: "tag", plural: "Tags", placeholder: "Search or create a tag…" },
  topics: { singular: "topic", plural: "Topics", placeholder: "Search or create a topic…" },
};

export function TagCategoryPicker({ kind, workspaceId, value, onChange, onManage }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const meta = labels[kind];

  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      if (kind === "tags") {
        const { data } = await supabase
          .from("tags")
          .select("name")
          .eq("workspace_id", workspaceId)
          .order("name");
        setSuggestions(((data ?? []) as Array<{ name: string }>).map((r) => r.name));
      } else {
        // Topics are now sourced from the Codex Framework Topic Tree.
        // See mem://features/topics-are-codex-driven.md.
        const { data } = await supabase
          .from("content_framework_topics")
          .select("label")
          .eq("workspace_id", workspaceId)
          .order("label");
        setSuggestions(((data ?? []) as Array<{ label: string }>).map((r) => r.label));
      }
    })();
  }, [kind, workspaceId]);

  const norm = (s: string) => s.trim().toLowerCase();
  const isSelected = (name: string) => value.some(v => norm(v) === norm(name));

  const toggle = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (isSelected(trimmed)) {
      onChange(value.filter(v => norm(v) !== norm(trimmed)));
    } else {
      onChange([...value, trimmed]);
      if (!suggestions.some(s => norm(s) === norm(trimmed))) {
        setSuggestions([...suggestions, trimmed].sort((a, b) => a.localeCompare(b)));
      }
    }
    setQuery("");
  };

  const remove = (name: string) => onChange(value.filter(v => v !== name));

  const q = query.trim();
  const filtered = suggestions.filter(s => s.toLowerCase().includes(q.toLowerCase()));
  const exactMatch = q && suggestions.some(s => norm(s) === norm(q));
  const showCreate = q.length > 0 && !exactMatch && !isSelected(q);

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map(name => (
            <Badge key={name} variant="secondary" className="gap-1 pr-1">
              {name}
              <button
                type="button"
                onClick={() => remove(name)}
                className="rounded-sm hover:bg-muted-foreground/20 p-0.5"
                aria-label={`Remove ${name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal text-muted-foreground"
          >
            {value.length > 0 ? `${value.length} selected` : `Add ${meta.plural.toLowerCase()}…`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={meta.placeholder}
              value={query}
              onValueChange={setQuery}
              onKeyDown={(e) => {
                if (e.key === "Enter" && showCreate) {
                  e.preventDefault();
                  toggle(q);
                }
              }}
            />
            <CommandList>
              {filtered.length === 0 && !showCreate && (
                <CommandEmpty>No {meta.plural.toLowerCase()} found.</CommandEmpty>
              )}
              {filtered.length > 0 && (
                <CommandGroup heading={meta.plural}>
                  {filtered.map(name => {
                    const selected = isSelected(name);
                    return (
                      <CommandItem key={name} value={name} onSelect={() => toggle(name)}>
                        <Check className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")} />
                        {name}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {showCreate && (
                <CommandGroup>
                  <CommandItem value={`__create_${q}`} onSelect={() => toggle(q)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create "{q}"
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
            {onManage && kind === "tags" && (
              <div className="border-t border-border p-1">
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onManage();
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  Manage tags…
                </button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
